import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Typography,
} from "@mui/material";
import {
  Air,
  CloudOutlined,
  LocationOnOutlined,
  Thermostat,
  // WaterDropOutlined,
} from "@mui/icons-material";
import { toast } from "react-toastify";
import axios from "axios";
import { AdminAction } from "../../components/admin";
import { API_ORIGIN as API_URL } from "../../config/env";

const defaultSelection = {
  language_id: "",
  state_name: "",
  district_name: "",
  station_code: "",
};

function getAuthHeaders() {
  return { rf_token_header123_k: localStorage.getItem("rfis") };
}

function buildForecastDays(forecast) {
  if (!forecast) return [];

  const observationDate = forecast.observation_date
    ? new Date(forecast.observation_date)
    : new Date();

  return Array.from({ length: 7 }, (_, index) => {
    const day = index + 1;
    const date = new Date(observationDate);
    date.setDate(observationDate.getDate() + index);

    return {
      day,
      date,
      maxTemp: forecast[`day_${day}_max_temp`],
      minTemp: forecast[`day_${day}_min_temp`],
      forecast: forecast[`day_${day}_forecast`],
    };
  });
}

export default function WeatherPage() {
  const [languages, setLanguages] = useState([]);
  const [states, setStates] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [stations, setStations] = useState([]);
  const [selection, setSelection] = useState(defaultSelection);
  const [forecast, setForecast] = useState(null);
  const [weatherError, setWeatherError] = useState("");
  const [loading, setLoading] = useState({
    languages: false,
    states: false,
    districts: false,
    stations: false,
    forecast: false,
  });

  const forecastDays = useMemo(() => buildForecastDays(forecast), [forecast]);
  const selectedStation = stations.find(
    (station) => String(station.station_code) === String(selection.station_code)
  );

  const setLoadingKey = useCallback((key, value) => {
    setLoading((current) => ({ ...current, [key]: value }));
  }, []);

  const fetchWeatherList = useCallback(async (path, params, loadingKey) => {
    setLoadingKey(loadingKey, true);
    setWeatherError("");
    try {
      const res = await axios.get(`${API_URL}/api/v1/weather/${path}`, {
        headers: getAuthHeaders(),
        params,
      });
      return Array.isArray(res.data.data) ? res.data.data : [];
    } catch (err) {
      const message =
        err.response?.data?.message ||
        "Weather service is currently unavailable. Please try again.";
      setWeatherError(message);
      toast.error(message);
      return [];
    } finally {
      setLoadingKey(loadingKey, false);
    }
  }, [setLoadingKey]);

  useEffect(() => {
    let isMounted = true;

    async function loadLanguages() {
      const data = await fetchWeatherList("languages", {}, "languages");
      if (!isMounted) return;

      setLanguages(data);
      const hindi = data.find((item) => item.language_desc === "Hindi");
      const english = data.find((item) => item.language_desc === "English");
      const defaultLanguage = hindi || english || data[0];
      if (defaultLanguage) {
        setSelection((current) => ({
          ...current,
          language_id: defaultLanguage.language_id,
        }));
      }
    }

    loadLanguages();

    return () => {
      isMounted = false;
    };
  }, [fetchWeatherList]);

  useEffect(() => {
    if (!selection.language_id) return;

    async function loadStates() {
      setStates([]);
      setDistricts([]);
      setStations([]);
      setForecast(null);
      setSelection((current) => ({
        ...current,
        state_name: "",
        district_name: "",
        station_code: "",
      }));

      const data = await fetchWeatherList(
        "states",
        { language_id: selection.language_id },
        "states"
      );
      setStates(data);
    }

    loadStates();
  }, [fetchWeatherList, selection.language_id]);

  const refreshWeatherSetup = () => {
    setSelection(defaultSelection);
    setStates([]);
    setDistricts([]);
    setStations([]);
    setForecast(null);
    setWeatherError("");

    fetchWeatherList("languages", {}, "languages").then((data) => {
      setLanguages(data);
      const hindi = data.find((item) => item.language_desc === "Hindi");
      const english = data.find((item) => item.language_desc === "English");
      const defaultLanguage = hindi || english || data[0];
      if (defaultLanguage) {
        setSelection((current) => ({
          ...current,
          language_id: defaultLanguage.language_id,
        }));
      }
    });
  };

  const handleFieldChange = async (field, value) => {
    if (field === "state_name") {
      setSelection((current) => ({
        ...current,
        state_name: value,
        district_name: "",
        station_code: "",
      }));
      setDistricts([]);
      setStations([]);
      setForecast(null);

      const data = await fetchWeatherList(
        "districts",
        { language_id: selection.language_id, state_name: value },
        "districts"
      );
      setDistricts(data);
      return;
    }

    if (field === "district_name") {
      setSelection((current) => ({
        ...current,
        district_name: value,
        station_code: "",
      }));
      setStations([]);
      setForecast(null);

      const data = await fetchWeatherList(
        "stations",
        {
          language_id: selection.language_id,
          state_name: selection.state_name,
          district_name: value,
        },
        "stations"
      );
      setStations(data);
      return;
    }

    setSelection((current) => ({ ...current, [field]: value }));
    setForecast(null);
  };

  const fetchForecast = async () => {
    if (
      !selection.language_id ||
      !selection.state_name ||
      !selection.district_name ||
      !selection.station_code
    ) {
      toast.error("Select language, state, district and station");
      return;
    }

    setLoadingKey("forecast", true);
    setWeatherError("");
    try {
      const res = await axios.get(`${API_URL}/api/v1/weather/forecast`, {
        headers: getAuthHeaders(),
        params: selection,
      });
      const nextForecast = Array.isArray(res.data.data) ? res.data.data[0] : null;
      setForecast(nextForecast);
      if (!nextForecast) {
        setWeatherError("No forecast data found for the selected station.");
      }
    } catch (err) {
      const message =
        err.response?.data?.message ||
        "Failed to fetch weather forecast. Please try again.";
      setWeatherError(message);
      toast.error(message);
    } finally {
      setLoadingKey("forecast", false);
    }
  };

  const isBusy = Object.values(loading).some(Boolean);

  return (
    <Box sx={{ p: 3 }}>
      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 3 }}>
        <CloudOutlined color="primary" />
        <Box>
          <Typography variant="h5" fontWeight={700}>
            Weather Forecast
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Station-wise 7-day forecast from the client weather service
          </Typography>
        </Box>
      </Stack>

      {weatherError && (
        <Alert
          severity="warning"
          sx={{ mb: 3 }}
          action={
            <Button color="inherit" size="small" onClick={refreshWeatherSetup}>
              Retry
            </Button>
          }
        >
          {weatherError}
        </Alert>
      )}

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Language</InputLabel>
                <Select
                  label="Language"
                  value={selection.language_id}
                  onChange={(e) =>
                    handleFieldChange("language_id", e.target.value)
                  }
                  disabled={loading.languages}
                >
                  {languages.map((language) => (
                    <MenuItem
                      key={language.language_id}
                      value={language.language_id}
                    >
                      {language.language_desc}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>State</InputLabel>
                <Select
                  label="State"
                  value={selection.state_name}
                  onChange={(e) =>
                    handleFieldChange("state_name", e.target.value)
                  }
                  disabled={!states.length || loading.states}
                >
                  {states.map((state) => (
                    <MenuItem key={state.state_name} value={state.state_name}>
                      {state.state_code} ({state.state_name})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>District</InputLabel>
                <Select
                  label="District"
                  value={selection.district_name}
                  onChange={(e) =>
                    handleFieldChange("district_name", e.target.value)
                  }
                  disabled={!districts.length || loading.districts}
                >
                  {districts.map((district) => (
                    <MenuItem
                      key={district.district_name}
                      value={district.district_name}
                    >
                      {district.district_code} ({district.district_name})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Station</InputLabel>
                <Select
                  label="Station"
                  value={selection.station_code}
                  onChange={(e) =>
                    handleFieldChange("station_code", e.target.value)
                  }
                  disabled={!stations.length || loading.stations}
                >
                  {stations.map((station) => (
                    <MenuItem
                      key={station.station_code}
                      value={station.station_code}
                    >
                      {station.station_name} ({station.station_code})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <AdminAction
                variant="primary"
                startIcon={<LocationOnOutlined />}
                onClick={fetchForecast}
                disabled={isBusy}
                loading={loading.forecast}
              >
                Get Forecast
              </AdminAction>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {forecast && (
        <Card sx={{ mb: 3, bgcolor: "#f7faf8" }}>
          <CardContent>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={7}>
                <Typography variant="overline" color="text.secondary">
                  Observation Date
                </Typography>
                <Typography variant="h6" fontWeight={700}>
                  {new Date(forecast.observation_date).toLocaleDateString(
                    "en-IN",
                    { day: "numeric", month: "long", year: "numeric" }
                  )}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {selectedStation?.station_name || "Selected station"}{" "}
                  {forecast.latitude && forecast.longitude
                    ? `(${forecast.latitude}, ${forecast.longitude})`
                    : ""}
                </Typography>
              </Grid>
              <Grid item xs={12} md={5}>
                <Stack direction="row" spacing={2} justifyContent="flex-end">
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Thermostat color="error" fontSize="small" />
                    <Typography variant="body2">
                      Max {forecast.day_1_max_temp} C
                    </Typography>
                  </Stack>
                  <Stack direction="row" spacing={1} alignItems="center">
                    {/* <WaterDropOutlined color="primary" fontSize="small" /> */}
                    <Typography variant="body2">
                      Min {forecast.day_1_min_temp} C
                    </Typography>
                  </Stack>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Air color="action" fontSize="small" />
                    <Typography variant="body2">7 days</Typography>
                  </Stack>
                </Stack>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}

      {forecastDays.length > 0 ? (
        <Grid container spacing={2}>
          {forecastDays.map((item) => (
            <Grid item xs={12} sm={6} lg={3} xl={12 / 7} key={item.day}>
              <Card sx={{ height: "100%" }}>
                <CardContent>
                  <Typography variant="body2" color="text.secondary">
                    {item.date.toLocaleDateString("en-IN", {
                      weekday: "short",
                      day: "numeric",
                      month: "short",
                    })}
                  </Typography>
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ my: 1 }}>
                    <Thermostat color="error" />
                    <Typography variant="h6" fontWeight={700}>
                      {item.maxTemp} C
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      / {item.minTemp} C
                    </Typography>
                  </Stack>
                  <Typography variant="body2">{item.forecast || "-"}</Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      ) : (
        !isBusy && (
          <Alert severity="info">
            Choose a station and fetch its 7-day weather forecast.
          </Alert>
        )
      )}
    </Box>
  );
}
